import { lumi } from "@/lib/lumi"
import { privacyNotify } from "@/lib/privacy"
import type { AppNotification, Block, Follow } from "@/types"

export async function notify(input: {
  recipientId?: string
  actorId?: string
  type: AppNotification["type"]
  postId?: string
  commentId?: string
  text?: string
}) {
  const recipientId = input.recipientId || ""
  const actorId = input.actorId || ""
  if (!recipientId || !actorId || recipientId === actorId) return
  try {
    await privacyNotify({
      recipientId,
      type: input.type,
      postId: input.postId,
      commentId: input.commentId,
      text: input.text,
    })
  } catch (err) {
    console.error("Notify failed:", err)
  }
}

export async function listBlockedIds(userId?: string) {
  if (!userId) return [] as string[]
  try {
    const res = await lumi.entities.blocks.list({ filter: { blockerId: userId }, limit: 200 })
    return Array.from(
      new Set(
        ((res?.list || []) as Block[])
          .filter((item) => item.blockerId === userId && item.blockedId)
          .map((item) => item.blockedId),
      ),
    )
  } catch (err) {
    console.error("Blocked list failed:", err)
    return [] as string[]
  }
}

export async function blockAccount(userId: string, targetUserId: string) {
  if (!userId || !targetUserId || userId === targetUserId) return
  const now = new Date().toISOString()
  const existingRes = await lumi.entities.blocks.list({ filter: { blockerId: userId, blockedId: targetUserId }, limit: 5 })
  const already = ((existingRes?.list || []) as Block[]).find((item) => item.blockerId === userId && item.blockedId === targetUserId)
  if (!already) {
    await lumi.entities.blocks.create({
      blockerId: userId,
      blockedId: targetUserId,
      creator: userId,
      createdAt: now,
      updatedAt: now,
    })
  }
  const [outRes, inRes] = await Promise.all([
    lumi.entities.follows.list({ filter: { followerId: userId, followingId: targetUserId }, limit: 10 }),
    lumi.entities.follows.list({ filter: { followerId: targetUserId, followingId: userId }, limit: 10 }),
  ])
  const followIds = [...((outRes?.list || []) as Follow[]), ...((inRes?.list || []) as Follow[])]
    .filter((item) =>
      (item.followerId === userId && item.followingId === targetUserId)
      || (item.followerId === targetUserId && item.followingId === userId),
    )
    .map((item) => item._id)
    .filter(Boolean)
  if (followIds.length) await Promise.all(followIds.map((id) => lumi.entities.follows.delete(id)))
}

export async function unblockAccount(userId: string, targetUserId: string) {
  if (!userId || !targetUserId) return
  const res = await lumi.entities.blocks.list({ filter: { blockerId: userId, blockedId: targetUserId }, limit: 10 })
  const ids = ((res?.list || []) as Block[])
    .filter((item) => item.blockerId === userId && item.blockedId === targetUserId && item._id)
    .map((item) => item._id)
  if (ids.length) await Promise.all(ids.map((id) => lumi.entities.blocks.delete(id)))
}

export function groupNotifications(items: AppNotification[]) {
  const groups = new Map<string, AppNotification[]>()
  items.forEach((item) => {
    const key = `${item.type}:${item.postId || item.actorId}`
    const list = groups.get(key) || []
    list.push(item)
    groups.set(key, list)
  })
  return Array.from(groups.values()).map((group) => {
    const newest = [...group].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
    return { key: newest._id, newest, count: group.length, unread: group.some((item) => !item.read) }
  })
}
